import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./hooks/useAuth";

// Pages
import Home from "./pages/Home";
import News from "./pages/News";
import NewsDetail from "./pages/NewsDetail";
import Festivals from "./pages/Festivals";
import FestivalDetail from "./pages/FestivalDetail";
import Directors from "./pages/Directors";
import DirectorDetail from "./pages/DirectorDetail";
import Resources from "./pages/Resources";
import Workshops from "./pages/Workshops";
import WorkshopDetail from "./pages/WorkshopDetail";
import Films from "./pages/Films";
import FilmDetail from "./pages/FilmDetail";
import Events from "./pages/Events";
import About from "./pages/About";
import Join from "./pages/Join";
import Forum from "./pages/Forum";
import ForumPost from "./pages/ForumPost";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AdminDashboard from "./pages/AdminDashboard";
import AuthCallback from "./pages/AuthCallback";
import Submit from "./pages/Submit";
import Jobs from "./pages/Jobs";
import Members from "./pages/Members";
import Messages from "./pages/Messages";
import Profile from "./pages/Profile";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Support from "./pages/Support";
import ServerStatus from "./pages/ServerStatus";

// Layout
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL for auth_token (new Google OAuth callback) or session_id (legacy)
  const searchParams = new URLSearchParams(location.search);
  if (searchParams.has('auth_token') || location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="news" element={<News />} />
          <Route path="news/:id" element={<NewsDetail />} />
        <Route path="festivals" element={<Festivals />} />
        <Route path="festivals/:id" element={<FestivalDetail />} />
        <Route 
          path="resources" 
          element={
            <ProtectedRoute>
              <Resources />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="workshops" 
          element={
            <ProtectedRoute>
              <Workshops />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="workshops/:id" 
          element={
            <ProtectedRoute>
              <WorkshopDetail />
            </ProtectedRoute>
          } 
        />
        <Route path="films" element={<Films />} />
        <Route path="films/:id" element={<FilmDetail />} />
        <Route path="events" element={<Events />} />
        <Route path="about" element={<About />} />
        <Route path="join" element={<Join />} />
        <Route 
          path="forum" 
          element={
            <ProtectedRoute>
              <Forum />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="forum/:id" 
          element={
            <ProtectedRoute>
              <ForumPost />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="submit" 
          element={
            <ProtectedRoute>
              <Submit />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="jobs" 
          element={
            <ProtectedRoute>
              <Jobs />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="members" 
          element={
            <ProtectedRoute>
              <Members />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="messages" 
          element={
            <ProtectedRoute>
              <Messages />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="profile" 
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } 
        />
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="verify-email" element={<VerifyEmail />} />
        <Route path="terms" element={<Terms />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="support" element={<Support />} />
        <Route path="server-status" element={<ServerStatus />} />
        <Route 
          path="admin/*" 
          element={
            <ProtectedRoute requireAdmin>
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
      </Route>
    </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <div className="noise-overlay" />
          <AppRouter />
          <SpeedInsights />
          <Analytics />
          <Toaster position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
